import { z } from 'zod';

// ---------------------------------------------------------------------------
// Kind enum
// ---------------------------------------------------------------------------

export const EMAIL_TEMPLATE_KINDS = [
  'magic_link',
  'approval_link',
  'approval_summary',
  'client_feedback',
  'notification_digest',
  'notification',
  'approval_chase',
  'client_reply',
] as const;

export const EmailTemplateKindSchema = z.enum(EMAIL_TEMPLATE_KINDS);
export type EmailTemplateKind = z.infer<typeof EmailTemplateKindSchema>;

// ---------------------------------------------------------------------------
// Field descriptor
// ---------------------------------------------------------------------------

export interface EmailTemplateFieldDescriptor {
  name: string;
  /** Hungarian UI label (csak az első szó nagybetűs) */
  label: string;
}

// ---------------------------------------------------------------------------
// Per-kind registry entry
// ---------------------------------------------------------------------------

export interface EmailTemplateKindEntry {
  fields: EmailTemplateFieldDescriptor[];
  allowedVariables: string[];
}

// ---------------------------------------------------------------------------
// Registry — single source of truth
// ---------------------------------------------------------------------------

export const EMAIL_TEMPLATE_FIELDS: Record<EmailTemplateKind, EmailTemplateKindEntry> = {
  magic_link: {
    fields: [
      { name: 'subject', label: 'Tárgy' },
      { name: 'brandingHeader', label: 'Fejléc' },
      { name: 'greeting', label: 'Megszólítás' },
      { name: 'headline', label: 'Főcím' },
      { name: 'intro', label: 'Bevezető' },
      { name: 'buttonLabel', label: 'Gomb felirata' },
      { name: 'linkHelperText', label: 'Gomb-segédszöveg' },
      { name: 'footer', label: 'Lábléc' },
    ],
    allowedVariables: ['{{recipientName}}', '{{expiresInMinutes}}'],
  },
  approval_link: {
    // Two stages share this template but ask for different things, so the
    // headline and intro exist per stage. A single shared block could only fit
    // one of them, which is where a tester got stuck trying to reword it.
    fields: [
      { name: 'subject', label: 'Tárgy (örökölt, mindkét fázis)' },
      // The renderer honours a legacy suffix-less `intro` as the fallback for
      // both stages (see approval-link.ts) — an org that set the single-block
      // override before the per-stage split keeps its wording. It has to be a
      // registered field or the save route rejects the whole map: the editor
      // re-sends every stored key for the kind, so one unregistered legacy key
      // made EVERY save of both approval letters fail with "Validation failed".
      { name: 'intro', label: 'Bevezető (örökölt, mindkét fázis)' },
      { name: 'subjectTopics', label: 'Tárgy — témajavaslatok' },
      { name: 'subjectContent', label: 'Tárgy — végső tartalom' },
      { name: 'brandingHeader', label: 'Fejléc' },
      { name: 'headlineTopics', label: 'Főcím — témajavaslatok' },
      { name: 'introTopics', label: 'Bevezető — témajavaslatok' },
      { name: 'headlineContent', label: 'Főcím — végső tartalom' },
      { name: 'introContent', label: 'Bevezető — végső tartalom' },
      { name: 'buttonLabel', label: 'Gomb felirata' },
      { name: 'linkHelperText', label: 'Gomb-segédszöveg' },
      { name: 'footer', label: 'Lábléc' },
      { name: 'signOff', label: 'Elköszönés' },
    ],
    allowedVariables: ['{{hotelName}}'],
  },
  approval_summary: {
    fields: [
      { name: 'subjectPrefix', label: 'Tárgy előtag' },
      { name: 'brandingHeader', label: 'Fejléc' },
      { name: 'headline', label: 'Főcím' },
      { name: 'intro', label: 'Bevezető' },
      { name: 'footer', label: 'Lábléc' },
      { name: 'signOff', label: 'Elköszönés' },
    ],
    allowedVariables: ['{{hotelName}}', '{{periodLabel}}'],
  },
  client_feedback: {
    fields: [
      { name: 'subject', label: 'Tárgy' },
      { name: 'brandingHeader', label: 'Fejléc' },
      { name: 'headline', label: 'Főcím (örökölt, minden döntésre)' },
      { name: 'intro', label: 'Bevezető (örökölt, minden döntésre)' },
      { name: 'headlineApprove', label: 'Főcím — jóváhagyta' },
      { name: 'introApprove', label: 'Bevezető — jóváhagyta' },
      { name: 'headlineChanges', label: 'Főcím — módosítást kért' },
      { name: 'introChanges', label: 'Bevezető — módosítást kért' },
      { name: 'headlineReject', label: 'Főcím — elvetette' },
      { name: 'introReject', label: 'Bevezető — elvetette' },
      { name: 'footer', label: 'Lábléc' },
      { name: 'signOff', label: 'Elköszönés' },
    ],
    allowedVariables: ['{{hotelName}}', '{{periodLabel}}', '{{postTitle}}'],
  },
  notification_digest: {
    fields: [
      { name: 'subject', label: 'Tárgy' },
      { name: 'brandingHeader', label: 'Fejléc' },
      { name: 'intro', label: 'Bevezető' },
      { name: 'footer', label: 'Lábléc' },
    ],
    allowedVariables: ['{{count}}', '{{recipientName}}'],
  },
  notification: {
    fields: [
      { name: 'brandingHeader', label: 'Fejléc' },
      { name: 'subjectPrefix', label: 'Tárgy előtag' },
      { name: 'ctaLabel', label: 'Gomb felirata' },
      { name: 'linkHelperText', label: 'Gomb-segédszöveg' },
      { name: 'footer', label: 'Lábléc' },
      { name: 'signOff', label: 'Elköszönés (csak ügyfélnek)' },
    ],
    allowedVariables: [],
  },
  approval_chase: {
    fields: [
      { name: 'subject', label: 'Tárgy' },
      { name: 'brandingHeader', label: 'Fejléc' },
      { name: 'headline', label: 'Főcím' },
      { name: 'intro', label: 'Bevezető' },
      { name: 'ctaLabel', label: 'Gomb felirata' },
      { name: 'linkHelperText', label: 'Gomb-segédszöveg' },
      { name: 'footer', label: 'Lábléc' },
      { name: 'signOff', label: 'Elköszönés' },
    ],
    allowedVariables: ['{{hotelName}}', '{{requestDate}}'],
  },
  client_reply: {
    fields: [
      { name: 'subject', label: 'Tárgy' },
      { name: 'brandingHeader', label: 'Fejléc' },
      { name: 'headline', label: 'Főcím' },
      { name: 'intro', label: 'Bevezető' },
      { name: 'ctaLabel', label: 'Gomb felirata' },
      { name: 'footer', label: 'Lábléc' },
      { name: 'signOff', label: 'Elköszönés' },
    ],
    allowedVariables: ['{{hotelName}}'],
  },
};

// ---------------------------------------------------------------------------
// Helper: get allowed field names for a kind
// ---------------------------------------------------------------------------

export function getAllowedFieldNames(kind: EmailTemplateKind): string[] {
  return EMAIL_TEMPLATE_FIELDS[kind].fields.map((f) => f.name);
}

// ---------------------------------------------------------------------------
// UpdateEmailTemplate schema factory
//
// Design: `kind` comes from the route param (Task 5: PUT /admin/email-templates/:kind).
// The body contains only `fields: Record<string,string>`. We expose a factory
// `makeUpdateEmailTemplateSchema(kind)` that closes over the allowed field names so a
// NestJS ZodValidationPipe on the body DTO receives a fully-scoped schema without
// needing `kind` in the request body at all. The controller resolves `kind` from
// `@Param`, validates it with `EmailTemplateKindSchema.parse()`, then passes it here.
//
// Task 16b: an empty `fields` object IS allowed — it means "no overrides for
// this kind", and the service deletes the org's override row for it (a no-op
// if there was none). This is how the admin UI resets a letter back to the
// hard-coded defaults. Reachable through the *existing* PUT route rather than
// a new DELETE endpoint: the web save path already merges a letter's edits
// over the kind's full stored field map before sending the PUT body (letters
// can share a kind), so "the merged result for the whole kind is empty" is
// exactly the condition under which deleting the row is correct — no new
// merge logic needed on either side.
// ---------------------------------------------------------------------------

export function makeUpdateEmailTemplateSchema(_kind: EmailTemplateKind) {
  // Unknown keys are NOT a validation error — they are stripped when stored
  // (see `pickRegisteredFields` below, applied by the API's upsert).
  //
  // They used to be rejected, and that made the whole feature unusable: the
  // editor edits ONE letter but has to re-send the kind's entire stored field
  // map (two letters can share a kind, so anything else would wipe the
  // sibling's overrides). One key left behind by an older registry version
  // therefore failed EVERY save with a bare "Validation failed" — which is
  // exactly what happened in production with `approval_link`'s legacy `intro`.
  //
  // Filtering server-side instead of rejecting is both safer and self-healing:
  // the registry here is authoritative (a client-side filter would depend on an
  // `allowedFields` list arriving intact over the network, and could silently
  // drop a sibling letter's real override), and the stale key disappears from
  // storage on the next save. Values are still length-capped, and a key that no
  // template reads cannot affect a sent email either way.
  return z.object({
    fields: z.record(z.string(), z.string().max(4000)),
  });
}

/**
 * Keep only the fields the kind actually registers. Applied on the write path
 * so unregistered leftovers never reach storage.
 */
export function pickRegisteredFields(
  kind: EmailTemplateKind,
  fields: Record<string, string>,
): Record<string, string> {
  const allowed = new Set(getAllowedFieldNames(kind));
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(fields)) {
    if (allowed.has(name)) out[name] = value;
  }
  return out;
}

// Infer the body type from any kind (shape is the same: { fields: Record<string,string> })
export type UpdateEmailTemplateBody = z.infer<ReturnType<typeof makeUpdateEmailTemplateSchema>>;

// ---------------------------------------------------------------------------
// EmailTemplateDto — surface contract for Task 5 (BE) and Task 7 (FE)
//
// `defaults` is populated by the API from the hardcoded template DEFAULTS map.
// `allowedFields` and `allowedVariables` are derived from the registry here so
// the FE doesn't need to bundle its own copy of the registry — it receives them
// in every GET response and can drive the editor UI directly.
// ---------------------------------------------------------------------------

export interface EmailTemplateDto {
  kind: EmailTemplateKind;
  fields: Record<string, string>;
  defaults: Record<string, string>;
  allowedFields: EmailTemplateFieldDescriptor[];
  allowedVariables: string[];
  updatedAt: string | null;
}

// ---------------------------------------------------------------------------
// PreviewEmailLetterBody — Task 11
//
// Body for POST /admin/email-templates/letters/:letterId/preview. `fields`
// carries UNSAVED edits (may be a subset of the letter's fields — merged on
// top of any stored overrides server-side); `variant` selects a client_feedback
// decision or a notification_internal sample shape. The letter isn't known
// yet at schema-construction time (it comes from the route param), so this
// schema is generic; per-letter field-name allowlisting happens in the
// service, which does know the letter.
// ---------------------------------------------------------------------------

export const PreviewEmailLetterBodySchema = z.object({
  fields: z.record(z.string(), z.string().max(4000)),
  variant: z.string().optional(),
});
export type PreviewEmailLetterBody = z.infer<typeof PreviewEmailLetterBodySchema>;
